import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Pagination,
  Paper,
  Skeleton,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Add, Close, Groups } from '@mui/icons-material';

import type {
  CreateTravelerDto,
  TravelerResponseDto,
  UpdateTravelerDto,
} from '@bandido/shared/dtos';

import { SearchInput } from '@frontend/features/common/components/SearchInput/SearchInput';
import { useSnackBar } from '@frontend/features/common/components/SnackBarContext';

import {
  useAssociateCaseTraveler,
  useCaseTravelers,
  useCaseTravelerSuggestions,
  useCreateCaseTraveler,
  useRemoveCaseTraveler,
  useUpdateCaseTraveler,
} from '../queries';
import { travelerDisplayName } from '../travelerForm';
import {
  travelerLoadState,
  travelerPageWithinRange,
} from '../travelerUiState';
import { TravelerCard } from './TravelerCard';
import { TravelerFormDialog } from './TravelerFormDialog';

const PAGE_SIZE = 8;

interface Props {
  open: boolean;
  caseId: number;
  agencyId: number;
  hasClient: boolean;
  onClose: () => void;
}

export const CaseTravelersModal = ({
  open,
  caseId,
  agencyId,
  hasClient,
  onClose,
}: Props) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const narrowViewport = useMediaQuery(theme.breakpoints.down('sm'));
  const { addToast } = useSnackBar();
  const [search, setSearch] = useState('');
  const [rosterPage, setRosterPage] = useState(1);
  const [suggestionsPage, setSuggestionsPage] = useState(1);
  const [editingTraveler, setEditingTraveler] =
    useState<TravelerResponseDto>();
  const [formOpen, setFormOpen] = useState(false);
  const [formError, setFormError] = useState<string>();
  const [travelerToRemove, setTravelerToRemove] =
    useState<TravelerResponseDto>();

  const roster = useCaseTravelers({
    caseId,
    search,
    currentPage: rosterPage,
    pageSize: PAGE_SIZE,
    enabled: open,
  });
  const suggestions = useCaseTravelerSuggestions({
    caseId,
    search,
    currentPage: suggestionsPage,
    pageSize: PAGE_SIZE,
    enabled: open && hasClient,
  });
  const createTraveler = useCreateCaseTraveler(caseId);
  const updateTraveler = useUpdateCaseTraveler(caseId);
  const associateTraveler = useAssociateCaseTraveler(caseId);
  const removeTraveler = useRemoveCaseTraveler(caseId);

  const loadState = useMemo(
    () => travelerLoadState([roster.error, suggestions.error]),
    [roster.error, suggestions.error],
  );
  const busy =
    associateTraveler.isLoading ||
    removeTraveler.isLoading ||
    createTraveler.isLoading ||
    updateTraveler.isLoading;

  useEffect(() => {
    const validPage = travelerPageWithinRange(
      rosterPage,
      roster.data?.pages,
    );
    if (validPage !== rosterPage) setRosterPage(validPage);
  }, [roster.data?.pages, rosterPage]);

  useEffect(() => {
    const validPage = travelerPageWithinRange(
      suggestionsPage,
      suggestions.data?.pages,
    );
    if (validPage !== suggestionsPage) setSuggestionsPage(validPage);
  }, [suggestions.data?.pages, suggestionsPage]);

  const changeSearch = (value: string) => {
    setSearch(value);
    setRosterPage(1);
    setSuggestionsPage(1);
  };

  const openCreate = () => {
    setEditingTraveler(undefined);
    setFormError(undefined);
    setFormOpen(true);
  };

  const openEdit = (traveler: TravelerResponseDto) => {
    setEditingTraveler(traveler);
    setFormError(undefined);
    setFormOpen(true);
  };

  const saveTraveler = async (
    input: CreateTravelerDto | UpdateTravelerDto,
  ) => {
    setFormError(undefined);
    try {
      if (editingTraveler) {
        await updateTraveler.mutateAsync({
          travelerId: editingTraveler.id,
          input: input as UpdateTravelerDto,
        });
      } else {
        await createTraveler.mutateAsync(input as CreateTravelerDto);
      }
      setFormOpen(false);
      addToast({
        message: t('travelers.feedback.saved'),
        color: 'success',
      });
    } catch (_error) {
      setFormError(t('travelers.feedback.saveError'));
    }
  };

  const associate = async (traveler: TravelerResponseDto) => {
    try {
      await associateTraveler.mutateAsync(traveler.id);
      addToast({
        message: t('travelers.feedback.added', {
          name: travelerDisplayName(traveler),
        }),
        color: 'success',
      });
    } catch (_error) {
      addToast({
        message: t('travelers.feedback.addError'),
        color: 'error',
      });
    }
  };

  const remove = async () => {
    if (!travelerToRemove) return;
    try {
      await removeTraveler.mutateAsync(travelerToRemove.id);
      addToast({
        message: t('travelers.feedback.removed'),
        color: 'success',
      });
      setTravelerToRemove(undefined);
    } catch (_error) {
      addToast({
        message: t('travelers.feedback.removeError'),
        color: 'error',
      });
    }
  };

  const renderList = (
    items: TravelerResponseDto[] | undefined,
    mode: 'roster' | 'suggestion',
    loading: boolean,
  ) => {
    if (loading) {
      return (
        <Box display="flex" flexDirection="column" gap={1}>
          <Skeleton variant="rounded" height={88} />
          <Skeleton variant="rounded" height={88} />
        </Box>
      );
    }
    if (!items?.length) {
      return (
        <Typography color="text.secondary" variant="body2" py={2}>
          {search
            ? t('travelers.states.noResults')
            : mode === 'roster'
              ? t('travelers.states.emptyRoster')
              : t('travelers.states.emptySuggestions')}
        </Typography>
      );
    }
    return (
      <Box display="flex" flexDirection="column" gap={1.5}>
        {items.map((traveler) => (
          <TravelerCard
            key={traveler.id}
            traveler={traveler}
            mode={mode}
            busy={busy}
            onEdit={openEdit}
            onAssociate={associate}
            onRemove={setTravelerToRemove}
          />
        ))}
      </Box>
    );
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        fullScreen={narrowViewport}
        maxWidth="lg"
      >
        <DialogTitle display="flex" alignItems="center" gap={1}>
          <Groups color="primary" />
          <Box flex={1}>
            <Typography variant="h6">{t('travelers.title')}</Typography>
            <Typography variant="body2" color="text.secondary">
              {t('travelers.subtitle')}
            </Typography>
          </Box>
          <IconButton aria-label={t('common.close')} onClick={onClose}>
            <Close />
          </IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ minHeight: 480 }}>
          {loadState === 'forbidden' ? (
            <Alert severity="warning">
              {t('travelers.states.noPermission')}
            </Alert>
          ) : loadState === 'error' && roster.isError ? (
            <Alert severity="error" action={<Button onClick={() => roster.refetch()}>{t('common.retry')}</Button>}>
              {t('travelers.states.loadError')}
            </Alert>
          ) : (
            <>
              <Box
                display="flex"
                flexDirection={{ xs: 'column', sm: 'row' }}
                alignItems={{ xs: 'stretch', sm: 'center' }}
                gap={2}
                mb={2}
              >
                <Box flex={1}>
                  <SearchInput
                    placeholder={t('travelers.search')}
                    onSearch={changeSearch}
                  />
                </Box>
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={openCreate}
                  disabled={!hasClient}
                >
                  {t('travelers.actions.create')}
                </Button>
              </Box>

              {!hasClient && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  {t('travelers.states.noClient')}
                </Alert>
              )}

              <Box
                display="grid"
                gridTemplateColumns={{ xs: '1fr', md: '1fr 1fr' }}
                gap={2}
              >
                <Paper variant="outlined" sx={{ p: 2, minWidth: 0 }}>
                  <Typography variant="subtitle1" fontWeight={600}>
                    {t('travelers.sections.roster', {
                      count: roster.data?.count ?? 0,
                    })}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={2}>
                    {t('travelers.sections.rosterHint')}
                  </Typography>
                  {renderList(roster.data?.items, 'roster', roster.isLoading)}
                  {(roster.data?.pages ?? 0) > 1 && (
                    <Pagination
                      sx={{ mt: 2 }}
                      page={rosterPage}
                      count={roster.data?.pages ?? 1}
                      onChange={(_event, page) => setRosterPage(page)}
                      size="small"
                    />
                  )}
                </Paper>

                <Paper variant="outlined" sx={{ p: 2, minWidth: 0 }}>
                  <Typography variant="subtitle1" fontWeight={600}>
                    {t('travelers.sections.known', {
                      count: suggestions.data?.count ?? 0,
                    })}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={2}>
                    {t('travelers.sections.knownHint')}
                  </Typography>
                  {!hasClient ? null : suggestions.isError ? (
                    <Alert severity="error">
                      {t('travelers.states.suggestionsError')}
                    </Alert>
                  ) : (
                    renderList(
                      suggestions.data?.items,
                      'suggestion',
                      suggestions.isLoading,
                    )
                  )}
                  {(suggestions.data?.pages ?? 0) > 1 && (
                    <Pagination
                      sx={{ mt: 2 }}
                      page={suggestionsPage}
                      count={suggestions.data?.pages ?? 1}
                      onChange={(_event, page) => setSuggestionsPage(page)}
                      size="small"
                    />
                  )}
                </Paper>
              </Box>
            </>
          )}
        </DialogContent>
      </Dialog>

      {formOpen && (
        <TravelerFormDialog
          open
          agencyId={agencyId}
          traveler={editingTraveler}
          isSaving={createTraveler.isLoading || updateTraveler.isLoading}
          error={formError}
          onClose={() => setFormOpen(false)}
          onSave={saveTraveler}
        />
      )}

      <Dialog
        open={Boolean(travelerToRemove)}
        onClose={() => setTravelerToRemove(undefined)}
        maxWidth="xs"
      >
        <DialogTitle>{t('travelers.remove.title')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('travelers.remove.description', {
              name: travelerToRemove
                ? travelerDisplayName(travelerToRemove)
                : '',
            })}
          </Typography>
          <Alert severity="info" sx={{ mt: 2 }}>
            {t('travelers.remove.keepProfile')}
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTravelerToRemove(undefined)}>
            {t('common.cancel')}
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={remove}
            disabled={removeTraveler.isLoading}
          >
            {t('travelers.actions.remove')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
